import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProductData } from '@elfsight/lib-stripe-integration';
import {
  CheckoutLoadingContainer,
  PaymentErrorMessage,
  Section,
  toStripeAmount,
  usePaymentSessionMetadata,
  useStripe
} from '@elfsight/lib-stripe-integration';
import { Show } from '@elfsight/lib-utils';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import { noop } from 'es-toolkit';
import type { PaymentFormProps } from '../../../common';
import { useCheckoutColors, useCheckoutI18n } from '../providers';
import { StripeCheckoutElements } from './checkout-elements';
import { useCheckoutProviderValue } from './use-checkout-provider-value';
import { usePaymentAppearances } from './use-payment-appearances';

export function StripePaymentForm({
  productData: rawProductData,
  customerEmail,
  paymentMetadata,
  onPaymentSuccess = noop
}: PaymentFormProps) {
  const { t } = useCheckoutI18n();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const { isPaymentInProcess } = usePaymentSessionMetadata();
  const { textColor, errorColor } = useCheckoutColors();
  const { appearance, disabledAppearance } = usePaymentAppearances();

  const handleStripeLoadError = useCallback(
    () => setError(t('Unable to load Stripe')),
    [t]
  );

  const stripe = useStripe(handleStripeLoadError);

  const stripeProductData: ProductData = useMemo(
    () => ({ ...rawProductData, amount: toStripeAmount(rawProductData) }),
    [rawProductData]
  );

  const handleFetchClientSecretError = useCallback(
    () =>
      setError(
        t(
          'We’re sorry, the payment page isn’t available right now. Please contact us for assistance.'
        )
      ),
    [t]
  );

  const { fetchClientSecret, checkoutKey } = useCheckoutProviderValue({
    stripeProductData,
    paymentMetadata,
    customerEmail,
    handleError: handleFetchClientSecretError
  });

  useEffect(() => {
    setIsLoading(true);
  }, [checkoutKey]);

  return (
    <CheckoutLoadingContainer
      isLoading={isLoading && !error}
      textColor={textColor}
      withDataSections={false}
      tabsGap={12}
      withCondensedFields
    >
      <CheckoutProvider
        key={checkoutKey}
        stripe={stripe}
        options={{
          fetchClientSecret,
          elementsOptions: {
            appearance: isPaymentInProcess ? disabledAppearance : appearance,
            savedPaymentMethod: {
              enableSave: 'auto',
              enableRedisplay: 'auto'
            },
            loader: 'always'
          }
        }}
      >
        {/* здесь используем именно rawProductData специально, 
        чтобы в сабмит формы попадало "display"-значение цены, т.е. без лишних нулей */}
        <StripeCheckoutElements
          onPaymentSuccess={onPaymentSuccess}
          productData={rawProductData}
          onReady={() => setIsLoading(false)}
        />
      </CheckoutProvider>

      <Show when={!!error}>
        <Section>
          <PaymentErrorMessage color={errorColor}>{error}</PaymentErrorMessage>
        </Section>
      </Show>
    </CheckoutLoadingContainer>
  );
}
